/**
 * Document titles — DESIGN.md §13.1 (SHELL-4, KEYS-13).
 *
 * Every tab said "Slate", which makes a second window, a browser history entry
 * and a bookmark useless. Titles are *claimed* rather than assigned: the route
 * claims a low-priority title, the open card claims a higher one, and the
 * highest live claim is what the tab shows. That way closing a card peek falls
 * back to the route title on its own, and two components never fight over
 * `document.title` in an order that depends on effect scheduling.
 */
import { useEffect } from 'react';

export const APP_NAME = 'Slate';

export const TITLE_PRIORITY = {
  /** Derived from the pathname, for routes that say nothing better. */
  route: 0,
  /** A route that knows its own subject (a project's name, a report). */
  page: 10,
  /** The open card wins: it is what the reader is looking at. */
  card: 20,
} as const;

interface Claim {
  id: number;
  priority: number;
  title: string;
}

let claims: Claim[] = [];
let nextId = 1;

function render(): void {
  // Last claim wins ties, so a newly opened surface beats the one it covers.
  let best: Claim | undefined;
  for (const claim of claims) {
    if (!best || claim.priority >= best.priority) best = claim;
  }
  document.title = best ? `${best.title} · ${APP_NAME}` : APP_NAME;
}

/**
 * Sets `<title>` to `${title} · Slate` while this component is mounted and the
 * claim outranks any other. Pass `undefined` to claim nothing (a loading route
 * should leave the previous title alone rather than flash "undefined").
 */
export function useDocumentTitle(
  title: string | undefined,
  priority: number = TITLE_PRIORITY.page,
): void {
  useEffect(() => {
    if (title === undefined || title === '') return;
    const claim: Claim = { id: nextId++, priority, title };
    claims = [...claims, claim];
    render();
    return () => {
      claims = claims.filter((entry) => entry.id !== claim.id);
      render();
    };
  }, [title, priority]);
}

/** `/admin/lookups` → `Admin · Lookups`, and so on. */
const ADMIN_TITLES: Record<string, string> = {
  users: 'Users',
  groups: 'Groups',
  lookups: 'Lookups',
  automations: 'Automations',
  tokens: 'Tokens',
  export: 'Export',
};

const STATIC_TITLES: Record<string, string> = {
  '/': 'My Tasks',
  '/inbox': 'Inbox',
  '/search': 'Search',
  '/views': 'Views',
  '/projects': 'Projects',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/login': 'Sign in',
};

/**
 * The fallback title for a pathname. `undefined` means "the route names
 * itself" (a card, a saved view) and nothing is claimed at route priority.
 */
export function routeTitle(pathname: string): string | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const staticTitle = STATIC_TITLES[path];
  if (staticTitle) return staticTitle;

  const project = /^\/projects\/([^/]+)/.exec(path);
  if (project?.[1]) return decodeURIComponent(project[1]);

  const admin = /^\/admin\/([^/]+)/.exec(path);
  if (admin?.[1]) return `Admin · ${ADMIN_TITLES[admin[1]] ?? 'Settings'}`;
  if (path.startsWith('/admin')) return 'Admin';

  if (path.startsWith('/reports')) return 'Reports';
  // `/card/12` is titled by the card route itself (`#12 Title · Slate`).
  return undefined;
}
